// pages/SingleRoleDetails.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Shield, Loader2, AlertCircle,
    FolderTree, Layers, ChevronDown, ChevronUp,
    Calendar, Hash, ToggleLeft, Edit3
} from 'lucide-react';

const API_BASE = 'https://api.mobilink.tech/api/superAdmin';
const API_TOKEN = sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken') || '';
const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN}` };

const DetailRow = ({ icon: Icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-orange-100 last:border-0">
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon size={14} style={{ color: '#FF7D38' }} />
        </div>
        <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
            <p className={`text-sm mt-0.5 break-all ${valueClass}`}>{value || '—'}</p>
        </div>
    </div>
);

const PermissionNode = ({ name, data, depth = 0 }) => {
    const [expanded, setExpanded] = useState(true);
    const isSection = data.Sections && !data.ComponentName;
    const actions = data.Actions || [];
    const displayName = data.ComponentName || data.title || name;

    const actionColors = {
        Add: 'bg-green-100 text-green-700 border-green-200',
        View: 'bg-blue-100 text-blue-700 border-blue-200',
        Edit: 'bg-orange-100 text-orange-700 border-orange-200',
        Delete: 'bg-red-100 text-red-700 border-red-200',
        Download: 'bg-purple-100 text-purple-700 border-purple-200',
        All: 'bg-orange-100 text-orange-700 border-orange-200'
    };

    return (
        <div className={`rounded-xl border border-orange-200 bg-white overflow-hidden ${depth > 0 ? 'ml-4 sm:ml-6 mt-2' : 'mt-2'}`}>
            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-orange-50 transition-colors"
                onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                        {isSection ? <FolderTree size={14} style={{ color: '#FF7D38' }} /> : <Layers size={14} style={{ color: '#FF7D38' }} />}
                    </div>
                    <span className="text-sm font-medium text-gray-800 truncate">{displayName}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {!isSection && actions.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200 font-medium">
                            {actions.length} action{actions.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                </div>
            </div>

            {expanded && !isSection && actions.length > 0 && (
                <div className="px-3 pb-3 pt-0 border-t border-orange-100">
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {actions.map(action => (
                            <span key={action} className={`text-[10px] px-2 py-1 rounded-md border font-medium ${actionColors[action] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                {action}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {expanded && isSection && data.Sections && (
                <div className="px-3 pb-3 pt-0 border-t border-orange-100">
                    {data.Sections.map((section, i) => (
                        <PermissionNode key={section.ComponentName || section.title || i}
                            name={section.ComponentName || section.title || `section-${i}`}
                            data={section} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

// Uses POST because browsers block GET with body
const fetchRoleById = async (id) => {
    const res = await fetch(`${API_BASE}/roles/getrolebyid`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ id })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Failed to fetch role (${res.status})`);
    }
    return res.json();
};

export const SingleRoleDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchRoleById(id)
            .then(data => {
                if (data.success && data.role) setRole(data.role);
                else setError(data.message || 'Role not found');
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [id]);

    const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    const permissionsObj = (() => {
        if (!role?.rolesAndPermission) return null;
        if (Array.isArray(role.rolesAndPermission)) return role.rolesAndPermission[0] || null;
        return role.rolesAndPermission;
    })();

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center rounded-2xl">
            <div className="flex flex-col items-center gap-3">
                <Loader2 size={32} className="animate-spin text-orange-500" />
                <p className="text-sm text-gray-500">Loading role details...</p>
            </div>
        </div>
    );

    if (error || !role) return (
        <div className="min-h-screen bg-gray-50 p-4 rounded-2xl">
            <div className="max-w-lg mx-auto mt-20 p-6 bg-white rounded-2xl border border-red-200 text-center">
                <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
                <h2 className="text-lg font-bold text-gray-800 mb-2">Role Not Found</h2>
                <p className="text-sm text-gray-500 mb-6">{error || 'The requested role could not be loaded.'}</p>
                <button onClick={() => navigate('/admin/roles')}
                    className="px-5 py-2.5 text-sm font-medium rounded-xl bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100">
                    ← Back to Roles
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 sm:p-5 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 flex-shrink-0">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{role.roleName}</h1>
                        <p className="text-xs text-gray-500 truncate">Role Details • ID: {role._id}</p>
                    </div>
                </div>
                <button onClick={() => navigate(`/admin/roles/edit/${role._id}`)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all whitespace-nowrap flex-shrink-0">
                    <Edit3 size={14} /> Edit Role
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Info Sidebar */}
                <div className="lg:col-span-1 bg-white rounded-2xl border border-orange-200 p-5 h-fit shadow-sm">
                    <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Shield size={16} style={{ color: '#FF7D38' }} /> Role Information
                    </h2>
                    {/* <DetailRow icon={Hash} label="Role ID" value={role._id} valueClass="text-xs font-mono text-gray-500" /> */}
                    <DetailRow icon={Shield} label="Role Name" value={role.roleName} valueClass="font-semibold text-gray-800" />
                    <DetailRow icon={ToggleLeft} label="Status"
                        value={
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${role.status === 'active'
                                    ? 'bg-green-50 text-green-600 border-green-200'
                                    : 'bg-gray-50 text-gray-500 border-gray-200'
                                }`}>{role.status}</span>
                        } />
                    <DetailRow icon={Calendar} label="Created" value={formatDate(role.createdAt)} valueClass="text-xs text-gray-600" />
                    <DetailRow icon={Calendar} label="Last Updated" value={formatDate(role.updatedAt)} valueClass="text-xs text-gray-600" />
                </div>

                {/* Permissions */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-orange-200 p-5 shadow-sm">
                    <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Layers size={16} style={{ color: '#FF7D38' }} /> Assigned Permissions
                    </h2>

                    {!permissionsObj || Object.keys(permissionsObj).length === 0 ? (
                        <div className="py-12 text-center">
                            <Layers size={36} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">No permissions assigned to this role</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {Object.entries(permissionsObj).map(([key, value]) => (
                                <PermissionNode key={key} name={key} data={value} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleRoleDetails;